import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { IconMoreVertical, IconPencilEdit, IconShare } from '@edulastic/icons'
import { orderBy } from 'lodash'
import { Checkbox, Dropdown, Icon, Tooltip } from 'antd'
import { folderSharing } from '@edulastic/constants'
import {
  getFoldersSelector,
  getSelectedItems,
  isOpenAddItemsModalSelector,
  isOpenRemovalModalSelector,
} from '../../selectors/folder'
import FolderRoleIcon from './FolderRoleIcon'
import { VIEWER_FOLDER_TOOLTIP } from './folderShareHelpers'
import {
  FolderListItem,
  FolderListItemTitle,
  CaretUp,
  DropMenu,
  MenuItems,
  MoreButton,
  FolderSelectionCheckbox,
  ModalFolderList,
  ModalFolderListItem,
  ModalFolderListItemTitle,
} from './styled'

const { FOLDER_ROLE } = folderSharing

const getEffectiveRole = (folder, canShare) => {
  if (folder.myRole) return folder.myRole
  if (!canShare) return FOLDER_ROLE.OWNER
  return folder.myRole
}

const getFolderTitle = (folder, myRole) => {
  const ownerName = folder?.owner?.name || 'Unknown'
  if (myRole === FOLDER_ROLE.OWNER) {
    return `Owner: ${ownerName}`
  }
  if (myRole === FOLDER_ROLE.EDITOR) {
    return `Owner: ${ownerName}; You have Editor Access.`
  }
  if (myRole === FOLDER_ROLE.VIEWER) {
    return `Owner: ${ownerName}; You have Viewer Access.`
  }
  return folder.folderName
}

const renderFolderIcon = ({ myRole, isActive, iconVariant }) => (
  <FolderRoleIcon role={myRole} active={isActive} variant={iconVariant} />
)

const FolderList = ({
  folders,
  folderId,
  ellipsis,
  showAllItems,
  selectFolder,
  showRenameModal,
  showDeleteConfirm,
  showShareModal,
  canShare,
  disableViewers,
  showSelectionCheckbox,
  pickerMode,
}) => {
  const iconVariant = pickerMode || showSelectionCheckbox ? 'modal' : 'sidebar'

  const menu = (folder) => {
    const myRole = getEffectiveRole(folder, canShare)
    const canEditMenu =
      !canShare || myRole === FOLDER_ROLE.OWNER || myRole === FOLDER_ROLE.EDITOR
    const canDelete =
      !canShare || myRole === FOLDER_ROLE.OWNER || !folder.myRole
    const showShare =
      canShare &&
      showShareModal &&
      (myRole === FOLDER_ROLE.OWNER || myRole === FOLDER_ROLE.EDITOR)

    return (
      <DropMenu>
        <CaretUp className="fa fa-caret-up" />
        {showShare && (
          <MenuItems
            data-cy="share"
            key="share"
            onClick={() => showShareModal(folder)}
          >
            <IconShare width={12} height={12} />
            <span>Share</span>
          </MenuItems>
        )}
        {canEditMenu && (
          <MenuItems
            data-cy="rename"
            key="rename"
            onClick={() => showRenameModal(folder)}
          >
            <IconPencilEdit width={12} height={12} />
            <span>Rename</span>
          </MenuItems>
        )}
        {canDelete && (
          <MenuItems
            data-cy="delete"
            key="delete"
            onClick={() => showDeleteConfirm(folder)}
          >
            <Icon type="close" /> <span>Delete</span>
          </MenuItems>
        )}
      </DropMenu>
    )
  }

  const shouldShowEllipsis = (folder) => {
    if (!ellipsis) return false
    if (!canShare) return true
    const myRole = getEffectiveRole(folder, canShare)
    return myRole === FOLDER_ROLE.OWNER || myRole === FOLDER_ROLE.EDITOR
  }

  const handleKeyboardSelect = (callback) => {
    return (event) => {
      if (event.key === 'Enter') {
        callback()
      }
    }
  }

  const handleFolderSelect = (folder, isViewerDisabled) => {
    if (isViewerDisabled) return
    const allowDeselect = pickerMode || showSelectionCheckbox
    if (allowDeselect && folderId && String(folder._id) === String(folderId)) {
      selectFolder({})
      return
    }
    selectFolder(folder)
  }

  const renderModalFolderRow = (folder) => {
    const isActive = folderId && String(folder._id) === String(folderId)
    const isViewerDisabled =
      disableViewers && folder.myRole === FOLDER_ROLE.VIEWER
    const myRole = getEffectiveRole(folder, canShare)

    const row = (
      <ModalFolderListItem
        data-cy={folder.folderName}
        key={folder._id}
        active={isActive}
        disabled={isViewerDisabled}
        aria-disabled={isViewerDisabled ? true : undefined}
        tabIndex={isViewerDisabled ? -1 : 0}
        onClick={() => handleFolderSelect(folder, isViewerDisabled)}
        onKeyDown={
          isViewerDisabled
            ? undefined
            : handleKeyboardSelect(() =>
                handleFolderSelect(folder, isViewerDisabled)
              )
        }
      >
        {showSelectionCheckbox && !isViewerDisabled && (
          <FolderSelectionCheckbox onClick={(event) => event.stopPropagation()}>
            <Checkbox
              checked={isActive}
              onChange={() => handleFolderSelect(folder, isViewerDisabled)}
              onClick={(event) => event.stopPropagation()}
              aria-label={`Select ${folder.folderName}`}
            />
          </FolderSelectionCheckbox>
        )}
        <ModalFolderListItemTitle title={folder.folderName}>
          {renderFolderIcon({
            myRole,
            isActive,
            iconVariant,
          })}
          <span>{folder.folderName}</span>
        </ModalFolderListItemTitle>
      </ModalFolderListItem>
    )

    if (isViewerDisabled) {
      return (
        <Tooltip key={folder._id} title={VIEWER_FOLDER_TOOLTIP}>
          <span>{row}</span>
        </Tooltip>
      )
    }

    return row
  }

  if (pickerMode || showSelectionCheckbox) {
    return (
      <ModalFolderList>
        {orderBy(folders, ['folderName'], ['asc']).map(renderModalFolderRow)}
      </ModalFolderList>
    )
  }

  return (
    <>
      {showAllItems && (
        <FolderListItem
          onClick={() => selectFolder(null)}
          active={!folderId}
          tabIndex={0}
          onKeyDown={handleKeyboardSelect(() => selectFolder(null))}
        >
          <FolderListItemTitle ellipsis={ellipsis} title="All Assignments">
            {renderFolderIcon({
              myRole: FOLDER_ROLE.OWNER,
              isActive: !folderId,
              iconVariant: 'sidebar',
            })}
            <span>All Assignments</span>
          </FolderListItemTitle>
        </FolderListItem>
      )}
      {orderBy(folders, ['folderName'], ['asc']).map((folder, index) => {
        const isActive = folder._id === folderId
        const myRole = getEffectiveRole(folder, canShare)
        const isViewerDisabled =
          disableViewers && folder.myRole === FOLDER_ROLE.VIEWER
        const folderTitle = getFolderTitle(folder, myRole)

        return (
          <FolderListItem
            data-cy={folder.folderName}
            key={index}
            active={isActive}
            tabIndex={isViewerDisabled ? -1 : 0}
            disabled={isViewerDisabled}
            aria-disabled={isViewerDisabled ? true : undefined}
            onKeyDown={
              isViewerDisabled
                ? undefined
                : handleKeyboardSelect(() =>
                    handleFolderSelect(folder, isViewerDisabled)
                  )
            }
          >
            <FolderListItemTitle
              ellipsis={ellipsis}
              title={folderTitle}
              onClick={() => handleFolderSelect(folder, isViewerDisabled)}
              style={isViewerDisabled ? { pointerEvents: 'none' } : undefined}
            >
              {renderFolderIcon({
                myRole,
                isActive,
                iconVariant,
              })}
              <span>{folder.folderName}</span>
            </FolderListItemTitle>
            {shouldShowEllipsis(folder) && (
              <Dropdown
                overlay={menu(folder)}
                trigger={['click']}
                placement="bottomRight"
              >
                <MoreButton data-cy="folder-option" active={isActive}>
                  <IconMoreVertical />
                </MoreButton>
              </Dropdown>
            )}
          </FolderListItem>
        )
      })}
    </>
  )
}

FolderList.propTypes = {
  showRenameModal: PropTypes.func,
  showDeleteConfirm: PropTypes.func,
  showShareModal: PropTypes.func,
  selectFolder: PropTypes.func,
  showAllItems: PropTypes.bool,
  folders: PropTypes.array,
  folderId: PropTypes.string,
  ellipsis: PropTypes.bool,
  canShare: PropTypes.bool,
  disableViewers: PropTypes.bool,
  showSelectionCheckbox: PropTypes.bool,
  pickerMode: PropTypes.bool,
}

FolderList.defaultProps = {
  showRenameModal: () => null,
  showDeleteConfirm: () => null,
  showShareModal: () => null,
  selectFolder: () => null,
  showAllItems: false,
  folders: [],
  folderId: '',
  ellipsis: false,
  canShare: false,
  disableViewers: false,
  showSelectionCheckbox: false,
  pickerMode: false,
}

export default connect(
  (state) => ({
    folders: getFoldersSelector(state),
    selectedItems: getSelectedItems(state),
    isOpenAddModal: isOpenAddItemsModalSelector(state),
    isOpenRemovalModal: isOpenRemovalModalSelector(state),
  }),
  {}
)(FolderList)
